import { SocketUpdateModel } from '../../components/smartsend/SmartSendSocketsModels';
import { SmartSendFilterContext, SmartSendSummary } from '../helpers/SmartSendFilterApplier';
import { ApplierOption } from '../helpers/ApplierOption';
import { IAppContextAction } from './BaseContextModels';
import { Customer, ExtraBase, SmartSendItemApiModel, Validation } from '~/lib/api/smartsend/types';
import { SmartSendSubType } from '../consts/SmartSendSubType';

export type ListFilter = {
  filter: SmartSendFilterContext;
  summary: SmartSendSummary;
};

export interface ISmartSendContext {
  customer: Customer;
  customerInitialised?: boolean;
  returnCustomer: Customer;
  filters: {
    [SmartSendSubType]: ListFilter;
    [key: string]: ListFilter;
  };
  deferredAction?: {
    [SmartSendSubType]?: ApplierOption;
    [key: string]: ApplierOption;
  };
}

export interface ICache {
  [key: string]: SmartSendItemState;
}

export interface SmartSendItemState extends SmartSendItemStateBase {
  open?: boolean;
  selected?: boolean;
  order: number;
}

export interface SmartSendItemStateBase {
  state: 'NEW' | 'INITIAL' | 'IDLE' | 'ERROR' | 'QUOTE' | 'QUOTING' | 'LOADING' | 'SAVE' | 'SAVING' | 'RETURN';
  validation?: Validation;
  unsaved: string[];
}

export interface SmartSendKeyedState {
  key: string;
  value: SmartSendItemState;
}

export function DefaultSmartSendItemState(): SmartSendItemState {
  return {
    open: false,
    selected: false,
    state: 'INITIAL',
    unsaved: [],
    order: 0,
    validation: {
      Errors: [],
      Warnings: [],
      Validated: false
    }
  };
}

export class EditItemStatusAction implements IAppContextAction {
  constructor(id: string, status: string, select: boolean) {
    this.payload = { id, status, select };
  }
  type = 'ITEM_ACTION_STATUS';
  payload: { id: string; status: string; select: boolean };
}

export class InitialiseCustomerAction implements IAppContextAction {
  constructor(customer: Customer) {
    this.payload = { customer };
  }
  type = 'INITIALISE_CUSTOMER';
  payload: { customer: Customer };
}

export class UpdateCustomerAction implements IAppContextAction {
  constructor(customer: Customer) {
    this.payload = { customer };
  }
  type = 'UPDATE_CUSTOMER';
  payload: { customer: Customer };
}

export class SetListyItemsAction implements IAppContextAction {
  constructor(items: SmartSendItemApiModel[], tab: string, take: number, skip: number) {
    this.payload = { items, tab, take, skip };
  }
  type = 'SET_LISTY_ITEMS';
  payload: {
    items: SmartSendItemApiModel[];
    tab: string;
    take: number;
    skip: number;
  };
}

export class SetPackingListSearchItemsAction implements IAppContextAction {
  constructor(items: SmartSendItemApiModel[], tab: string) {
    this.payload = { items, tab };
  }
  type = 'SET_PACKINGLISTSEARCH_ITEMS';
  payload: {
    items: SmartSendItemApiModel[];
    tab: string;
  };
}

export class SmartSendItemsAddedSocketAction implements IAppContextAction {
  constructor(items: SmartSendItemApiModel[], page: number) {
    this.payload = { items, page };
  }
  type = 'SOCKET:SMARTSEND_NEW_ITEMS';
  payload: { items: SmartSendItemApiModel[]; page: number };
}

export class SmartSendItemsRemovedSocketAction implements IAppContextAction {
  constructor(items: SmartSendItemApiModel[], page: number) {
    this.payload = { items, page };
  }
  type = 'SOCKET:SMARTSEND_REMOVE_ITEMS';
  payload: { items: SmartSendItemApiModel[]; page: number };
}

export class SmartSendItemsUpdatedSocketAction implements IAppContextAction {
  constructor(model: SocketUpdateModel) {
    this.payload = { model };
  }
  type = 'SOCKET:SMARTSEND_ITEMS_UPDATED';
  payload: { model: SocketUpdateModel };
}

export class SetProviderLimitAction implements IAppContextAction {
  constructor(providers: string[]) {
    this.payload = { providers };
  }
  type = 'SET_QUOTE_PROVIDERS';
  payload: { providers: string[] };
}

export class ApplyStatusToSelectedAction implements IAppContextAction {
  constructor(filterKey: string, status: string, resetSelection: boolean) {
    this.payload = {
      filterKey,
      status,
      resetSelection
    };
  }
  type = 'BULK_ACTION_STATUS';
  payload: {
    filterKey: string;
    status: string;
    resetSelection?: boolean;
  };
}

export class ApplyStateToItemsAction implements IAppContextAction {
  constructor(ids: string[], state: string) {
    this.payload = { ids, to: state };
  }
  type = 'UPDATE_ITEM_STATE';
  payload: {
    to: string;
    ids: string[];
  };
}

export class ArrangeReturnCollectionAction implements IAppContextAction {
  constructor(ids: string[]) {
    this.payload = ids;
  }
  type = 'ARRANGE_RETURN_COLLECTION';
  payload: string[];
}

export class MarkAsStagedItemsAction implements IAppContextAction {
  constructor(ids: string[]) {
    this.payload = { ids };
  }
  type = 'MARK_AS_STAGED';
  payload: {
    ids: string[];
  };
}

export class MarkAsPaidItemsTempAction implements IAppContextAction {
  constructor(ids: string[]) {
    this.payload = { ids };
  }
  type = 'MARK_AS_PAID_TEMP';
  payload: {
    ids: string[];
  };
}

export class MarkAsPaidItemsAction implements IAppContextAction {
  constructor(items: SmartSendItemApiModel[]) {
    this.payload = { items: items };
  }
  type = 'MARK_AS_PAID';
  payload: {
    items: SmartSendItemApiModel[];
  };
}

export class RemoveTagAction implements IAppContextAction {
  constructor(id: string, tag: string) {
    this.payload = { id, tag };
  }
  type = 'REMOVE_TAG';
  payload: {
    id: string;
    tag: string;
  };
}

export class SaveTagsAction implements IAppContextAction {
  constructor(ids: string[], tags: string[]) {
    this.payload = { ids, tags };
  }
  type = 'SAVE_ITEM_TAGS';
  payload: {
    ids: string[];
    tags: string[];
  };
}

export class UpdateItemCacheAction implements IAppContextAction {
  constructor(id: string, cache: SmartSendItemState) {
    this.payload = { key: id, value: cache };
  }
  type = 'UPDATE_ITEM_CACHE';
  payload: {
    key: string;
    value: SmartSendItemState;
  };
}

export class MarkPackagesItemAction implements IAppContextAction {
  constructor(ids: string[], packaged: boolean) {
    this.payload = {
      ids,
      packaged
    };
  }
  type = 'MARK_PACKAGED';
  payload: {
    ids: string[];
    packaged: boolean;
  };
}

export class MarkAllPackagedAction implements IAppContextAction {
  constructor() {
    this.payload = {};
  }
  type = 'MARK_ALL_PACKAGED';
  payload: object;
}

export class MarkDispatchedItemAction implements IAppContextAction {
  constructor(ids: string[], isRetry?: boolean) {
    this.payload = {
      ids,
      isRetry
    };
  }
  type = 'MARK_DISPATCHED';
  payload: {
    ids: string[];
    isRetry?: boolean;
  };
}

export class UpdateItemsCacheAction implements IAppContextAction {
  constructor(items: SmartSendKeyedState[]) {
    this.payload = { items };
  }
  type = 'UPDATE_ITEMS_CACHE';
  payload: {
    items: SmartSendKeyedState[];
  };
}

export class ToggleSelectionAction implements IAppContextAction {
  constructor(ids: string[], value: boolean) {
    this.payload = { ids, val: value };
  }
  type = 'TOGGLE_GROUP';
  payload: {
    ids: string[];
    val: boolean;
  };
}

export class ToggleSelectionWithActionAction implements IAppContextAction {
  constructor(ids: string[], value: boolean, action: ApplierOption) {
    this.payload = { ids, val: value, action };
  }
  type = 'TOGGLE_GROUP_WITH_ACTION';
  payload: {
    ids: string[];
    val: boolean;
    action: ApplierOption;
  };
}

export class ClearDeferredActionAction implements IAppContextAction {
  constructor(filterKey: string) {
    this.payload = { filterKey };
  }
  type = 'CLEAR_DEFERRED_ACTION';
  payload: {
    filterKey: string;
  };
}

export class ToggleSelectionAllItemsAction implements IAppContextAction {
  constructor(filterKey: string, value: boolean) {
    this.payload = {
      filterKey,
      value
    };
  }
  type = 'TOGGLE_ALL';
  payload: {
    filterKey: string;
    value: boolean;
  };
}

export class ToggleSelectionItemsAction implements IAppContextAction {
  constructor(filterKey: string, type: string, value: boolean) {
    this.payload = {
      filterKey: filterKey,
      value: value
    };
    this.type = type;
  }
  type: string;
  payload: {
    filterKey: string;
    value: boolean;
  };
}

export class ToggleSelectionSourceItemsAction implements IAppContextAction {
  constructor(filterKey: string, source: string, value: boolean) {
    this.payload = { filterKey: filterKey, value: value, source: source };
    this.type = 'TOGGLE_SOURCE';
  }
  type: string;
  payload: { filterKey: string; value: boolean; source: string };
}

export class ToggleSelectionProviderItemsAction implements IAppContextAction {
  constructor(filterKey: string, provider: string, value: boolean) {
    this.payload = { filterKey: filterKey, value: value, provider: provider };
    this.type = 'TOGGLE_PROVIDER';
  }
  type: string;
  payload: { filterKey: string; value: boolean; provider: string };
}

export class ToggleSelectionReadyItemsAction implements IAppContextAction {
  constructor(filteKey: string, value: boolean) {
    this.payload = {
      filterKey: filteKey,
      value: value
    };
  }
  type = 'TOGGLE_READY';
  payload: {
    value: boolean;
    filterKey: string;
  };
}

export class OnSendReadyItemsAction implements IAppContextAction {
  constructor(filterKey: string, filter: SmartSendFilterContext) {
    this.payload = {
      filterKey: filterKey,
      filter: filter
    };
  }
  type = 'SEND_READY';
  payload: {
    filterKey: string;
    filter: SmartSendFilterContext;
  };
}

export class ToggleSelectionDomesticItemsAction implements IAppContextAction {
  constructor(filterKey: string, value: boolean) {
    this.payload = {
      filterKey: filterKey,
      value: value
    };
  }
  type = 'TOGGLE_DOMESTIC';
  payload: {
    value: boolean;
    filterKey: string;
  };
}

export class ToggleSelectionInternationalItemsAction implements IAppContextAction {
  constructor(filterKey: string, value: boolean) {
    this.payload = {
      filterKey: filterKey,
      value: value
    };
  }
  type = 'TOGGLE_INTERNATIONAL';
  payload: {
    value: boolean;
    filterKey: string;
  };
}

export class ToggleSelectionNotReadyItemsAction implements IAppContextAction {
  constructor(filteKey: string, value: boolean) {
    this.payload = {
      filterKey: filteKey,
      value: value
    };
  }
  type = 'TOGGLE_NOTREADY';
  payload: {
    value: boolean;
    filterKey: string;
  };
}

export class UpdateFilterAction implements IAppContextAction {
  constructor(filterKey: string, filter: SmartSendFilterContext) {
    this.payload = {
      filterKey,
      filter
    };
  }
  type = 'UPDATE_FILTER';
  payload: {
    filterKey: string;
    filter: SmartSendFilterContext;
  };
}

export class CreateFilterAction implements IAppContextAction {
  constructor(filterKey: string, filter: SmartSendFilterContext) {
    this.payload = {
      filterKey,
      filter
    };
  }
  type = 'CREATE_FILTER';
  payload: {
    filterKey: string;
    filter: SmartSendFilterContext;
  };
}

export class SaveItemsAction implements IAppContextAction {
  constructor(ids: string[], actions: ApplierOption[], changes: { [key: string]: any }) {
    this.payload = {
      ids,
      actions,
      changes
    };
  }
  type = 'SAVE_ITEMS';
  payload: {
    ids: string[];

    actions: ApplierOption[];
    changes: { [key: string]: any };
  };
}

export class ReleaseItemsAction implements IAppContextAction {
  constructor(ids: string[]) {
    this.payload = {
      ids
    };
  }
  type = 'RELEASE_BOOKING';
  payload: {
    ids: string[];
  };
}

export class AddNewItemAction implements IAppContextAction {
  constructor(item: SmartSendItemApiModel, edit: boolean) {
    this.payload = {
      item,
      edit
    };
  }
  type = 'ADD_NEW_ITEM';
  payload: {
    item: SmartSendItemApiModel;
    edit: boolean;
  };
}

export class AddNewItemsAction implements IAppContextAction {
  constructor(items: SmartSendItemApiModel[]) {
    this.payload = {
      items
    };
  }
  type = 'ADD_NEW_ITEMS';
  payload: {
    items: SmartSendItemApiModel[];
  };
}

export class BulkSaveSelectedAction implements IAppContextAction {
  constructor(filterKey: string, actions: ApplierOption[], changes: { [key: string]: any }, force: boolean) {
    this.payload = {
      filterKey,
      changes,
      actions,
      force
    };
  }
  type = 'BULK_SAVE_SELECTED_ITEMS';
  payload: {
    filterKey: string;
    actions?: ApplierOption[];
    force: boolean;
    changes: { [key: string]: any };
  };
}

export class BulkAssignExtrasAction implements IAppContextAction {
  constructor(ids: string[], type: 'add' | 'remove', extras: ExtraBase[]) {
    this.payload = {
      ids,
      extras,
      type
    };
  }
  type = 'BULK_SAVE_EXTRAS';
  payload: {
    ids: string[];
    type: 'add' | 'remove';
    extras: ExtraBase[];
  };
}

export class ResetSelectionAction implements IAppContextAction {
  constructor(filterKey: string) {
    this.payload = {
      filterKey
    };
  }
  type = 'RESET_SELECTION';
  payload?: {
    filterKey: string;
  };
}
